import { captureException } from '@sentry/react';
import invariant from 'tiny-invariant';
import type { MachineOptions } from 'xstate';
import { assign, send } from 'xstate';

import { BuildingModel } from 'src/modules/designStudio/database/buildingModel';
import type { VersionDocument } from 'src/modules/designStudio/database/documentTypes';
import { FirebaseClient } from 'src/modules/designStudio/database/firebaseClient';
import type { Structure } from 'src/modules/designStudio/helpers/data/TemplateDesignConfig';
import {
  createEmptyTemplateDesignConfig,
  getCurrentDesignConfig,
  isTemplateDesignConfig,
} from 'src/modules/designStudio/helpers/data/TemplateDesignConfig';
import { handleAuthenticationException } from 'src/modules/designStudio/helpers/handleAuthenticationException';
import {
  hideEnvironment,
  makeTransparentWithOutlines,
  restoreOpacityAndHideOutlines,
  showEnvironment,
  takeScreenshot,
} from 'src/modules/designStudio/helpers/machete';
import { macheteBuildingRef } from 'src/modules/designStudio/machines/building/ref';
import {
  batchMarkVersionsAsNotCurrent,
  getLatestVersions,
  listenForVersions,
  listenForVersionsBySession,
} from 'src/modules/designStudio/machines/firebaseUtils';
import type {
  ClearInspireMeImage,
  Context,
  Event,
  SetHistoryEvent,
  SetInspireMeImage,
  SetupCompletionEvent,
  WaitForInspireMeImage,
} from 'src/modules/designStudio/machines/history/types';
import { sharedOptions } from 'src/modules/designStudio/machines/sharedOptions';
import { macheteViewerRef } from 'src/modules/designStudio/machines/viewer/ref';
import { Mode } from 'src/modules/designStudio/providers';
import { ToastType } from 'src/shared/components/Toast/types';
import { client as apolloClient } from 'src/shared/graphql';
import type { User } from 'src/shared/graphql/generated';
import { queries } from 'src/shared/graphql/requests';
import { request } from 'src/shared/helpers/request';
import { emitPageAction } from 'src/shared/telemetry';
import { EventType } from 'src/shared/telemetry/newrelic';
import { withFirebaseErrorSentry } from 'src/shared/telemetry/sentry';

interface GenerateImageResponse {
  input_path: string;
  image: string;
}

const SAVING_NOTIFICATION_INTERVAL = 15000;

const isNotBottomOfStack = ({ currentIndexInHistory, history }: Context) =>
  !!(currentIndexInHistory && currentIndexInHistory !== 0 && history?.length);
const isNotTopOfStack = ({ currentIndexInHistory, history }: Context) =>
  !!(
    currentIndexInHistory !== undefined &&
    history?.length &&
    currentIndexInHistory !== history.length - 1
  );
const markVersionAsCurrent = (
  { history, currentIndexInHistory }: Context,
  direction: 1 | -1,
) => {
  if (
    !history ||
    !history.length ||
    (!currentIndexInHistory && currentIndexInHistory !== 0)
  ) {
    return true;
  }
  const batch = batchMarkVersionsAsNotCurrent(history);
  const versionToBeMarkedAsCurrent = history[currentIndexInHistory + direction];

  batch.update(versionToBeMarkedAsCurrent.ref, { current: true });
  return withFirebaseErrorSentry(() => batch.commit())();
};

const optimisticUpdate = (
  { currentIndexInHistory, history }: Context,
  direction: -1 | 1,
) => {
  if (!currentIndexInHistory && currentIndexInHistory !== 0) {
    return {};
  }

  const nextIndexInHistory = currentIndexInHistory + direction;
  const currentVersion = history?.[nextIndexInHistory];

  /**
   * optimisticUpdate gets called in response to a undo/redo button press
   * Is it possible for designConfig to be legacy with the flag = 'on',
   * When we create a new design config to add to the history stack with flag = 'on',
   * the design config should be template.
   * Should we attempt to convert or error out?
   */
  const designConfig = currentVersion?.design_config;
  if (isTemplateDesignConfig(designConfig) && macheteBuildingRef.current) {
    macheteBuildingRef.current.designer
      .set_active_design(designConfig)
      .catch(error => captureException(error));
  }

  return {
    designConfig,
    currentVersion: currentVersion?.id,
  };
};
const createVersion = async (ctx: Context, designConfig: Structure) => {
  invariant(ctx.buildingModel, 'Missing from context: building model');
  invariant(ctx.buildingModel.ref, 'Missing from context: building model ref');
  invariant(ctx.userId, 'Missing from context: userId');
  invariant(ctx.sessionId, 'Missing from context: sessionId');
  const authorId = ctx.viewUserId || ctx.userId;

  if (window?.navigator?.onLine === false) {
    return undefined;
  }
  return ctx.buildingModel.createVersion(
    authorId,
    ctx.sessionId,
    ctx.history,
    designConfig,
    ctx.currentIndexInHistory,
  );
};
const fetchCurrentVersion = async (ctx: Context) => {
  const { buildingModel, userId, viewUserId, sessionId } = ctx;

  invariant(buildingModel, 'Missing from context: building model');
  invariant(buildingModel.ref, 'Missing from context: building model ref');
  invariant(userId, 'Missing from context: userId');
  const authorId = viewUserId || userId;
  let currentVersion;
  // fetch doc that has current boolean = true;
  const currentDocs = await buildingModel.getVersionMarkedAsCurrent(authorId);

  if (currentDocs && !currentDocs?.empty) {
    [currentVersion] = currentDocs.docs;
  }

  if (!currentVersion) {
    // if no doc above, fetch latest doc
    const latestDocs = await getLatestVersions(buildingModel.ref, authorId);
    if (latestDocs && !latestDocs.empty) {
      [currentVersion] = latestDocs.docs;
    }
  }

  if (typeof currentVersion?.ref !== 'undefined' && !viewUserId) {
    const cvRef = currentVersion.ref;
    await withFirebaseErrorSentry(() =>
      cvRef.update({
        session_id: sessionId,
        current: true,
      }),
    )();
  }
  // if no version history, create initial version with empty config
  if (!currentVersion) {
    await createVersion(ctx, createEmptyTemplateDesignConfig());
  }

  return currentVersion;
};
const options: MachineOptions<Context, Event> = {
  services: {
    spinBeforeRequestInspireMeImage: async () => {
      const viewer = macheteViewerRef.current;
      if (viewer) {
        const screenshotBlob = await takeScreenshot(viewer);
        return { frozenRenderURL: URL.createObjectURL(screenshotBlob) };
      }
      return {};
    },
    requestInspireMeImage: async ctx => {
      const viewer = macheteViewerRef.current;
      const building = macheteBuildingRef.current;

      if (
        building &&
        viewer &&
        ctx.mode !== Mode.GUEST &&
        ctx.mode !== Mode.SHARE
      ) {
        try {
          const response = await queries.profile(apolloClient, {});
          const user = response?.data?.profile?.user as User;

          const designNameToRestore =
            building.designer.get_active_design().to_config().designName || '';
          building.designer.view_design('');

          await viewer.resize(500);
          // Turn the model transparent and make outlines white.
          makeTransparentWithOutlines(building);
          // Disable sky and ground so that only the black background remains.
          hideEnvironment(viewer);
          // This resize call has 2 purposes:
          // - The 500ms delay is what allows the opacity change to finish before taking
          //   the screenshot (I hope 500ms is enough in all cases).
          // - A misalignment of the screenshot (and resulting image) and the render is
          //   caused by the fact that the resizing that takes place when opening the
          //   Inspire Me menu doesn't seem to finish; it leaves something inside Machete
          //   in unfinished state. This resize call completes the operation.
          // Ideally, Machete would expose a promise that tells us when the opacity changes finish. And we'd fix whatever is wrong with the resize call.
          await viewer.resize(500);
          const screenshotBlob = await takeScreenshot(viewer);
          const userEmail = user.email ? user.email : 'ds.gml@hover.to';

          const formData = new FormData();
          formData.set('lines_input', 'True');
          formData.set('email', userEmail);
          formData.set(
            'seed',
            `${Math.floor(Math.random() * Number.MAX_SAFE_INTEGER)}`,
          );
          formData.set('agree', 'yes');
          formData.set('style', 'Auto');
          formData.set('image_flexibility', '.6');
          formData.set('intensity', '0');
          formData.set('batch_size', '1');
          formData.set('image', screenshotBlob, 'screenshot.png');

          try {
            const res = await request(
              `https://inspireme.hover.to/upload_image`,
              {
                method: 'POST',
                body: formData,
              },
            );

            const { image } =
              // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
              (await res.json()) as GenerateImageResponse;

            return { image, designNameToRestore };
          } catch (e) {
            restoreOpacityAndHideOutlines(building);
            showEnvironment(viewer);
            building.designer.view_design(designNameToRestore);

            // logError is the state machine action upon error.
            throw e;
          }
        } catch (e) {
          handleAuthenticationException(e);
        }
      }

      return {};
    },
    fetchCurrentVersion,
    setupNotifierService: () => callback => {
      const intervalId = setInterval(() => {
        callback('NOTIFY_SAVE');
      }, SAVING_NOTIFICATION_INTERVAL);

      return () => clearInterval(intervalId);
    },
    markPreviousDesignAsCurrent: async (ctx: Context) =>
      markVersionAsCurrent(ctx, -1),
    markNextDesignAsCurrent: async (ctx: Context) =>
      markVersionAsCurrent(ctx, 1),
    setupAndFetchModelReference: async (ctx: Context) => {
      let userId: number | undefined;
      let email: string | undefined;
      let user: User | undefined;
      let partnerId: number | undefined;
      let { authToken } = ctx;

      invariant(ctx.jobId, 'No job id was provided');

      try {
        if (ctx.mode === Mode.SHARE) {
          const { data } = await queries.jobSharedThreeDFiles(apolloClient, {
            jobId: ctx.jobId,
            shareToken: ctx.shareToken || '',
          });

          const resp = data?.jobSharedThreeDFiles ?? {};
          authToken = resp.authToken;
        } else if (!ctx.authToken) {
          const response = await queries.profile(apolloClient, {});
          user = response?.data?.profile?.user as User;

          userId = user.id;
          email = user.email;
          partnerId = user.orgs[0]?.partner?.id;
        }
      } catch (e) {
        handleAuthenticationException(e);
      }

      try {
        await FirebaseClient.authenticate(
          authToken,
          ctx.jobId,
          // used for in the pro view to fetch design config
          // not authored by the logged in user
          ctx.viewUserId,
        );
      } catch (e) {
        throw new Error(
          'Unable to authenticate with firebase - using guest mode',
        );
      }

      const buildingModel = new BuildingModel(ctx.jobId);
      await buildingModel.getBuildingModelReference();

      if (!userId && ctx.shareToken) {
        const shareData = await buildingModel.getShare(ctx.shareToken);
        invariant(shareData, 'Share data could not be found');
        const { author_id: id } = shareData;
        userId = id;
      }

      return {
        user,
        email,
        buildingModel,
        userId,
        partnerId,
      };
    },
    fetchVersions:
      (ctx: Context) => (callback: (event: SetHistoryEvent) => void) => {
        const { buildingModel, userId, sessionId, shareToken, viewUserId } =
          ctx;
        const authorId = viewUserId || userId;
        invariant(buildingModel, 'Missing from context: building model');
        invariant(
          buildingModel.ref,
          'Missing from context: building model ref',
        );
        return shareToken || viewUserId
          ? listenForVersions({
              ref: buildingModel.ref,
              userId: authorId,
              callback,
            })
          : listenForVersionsBySession({
              ref: buildingModel.ref,
              userId: authorId,
              callback,
              sessionId,
            });
      },
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    createVersion: async (ctx: Context, _: Event) => {
      const { designConfig } = ctx;
      await createVersion(ctx, designConfig);
    },
  },
  guards: {
    canUndo: isNotBottomOfStack,
    canRedo: isNotTopOfStack,
    isShareView: ctx => !!ctx.shareToken,
    shouldNotifySave: ctx => ctx.changeSinceLastNotification,
  },
  actions: {
    // @ts-ignore
    resetChangeSinceNotification: assign({
      changeSinceLastNotification: false,
    }),
    // @ts-ignore
    markChangeSinceNotification: assign({
      changeSinceLastNotification: true,
    }),
    sendDoneFetchingLatestVersionEvent: (ctx: Context) => {
      const attrs = {
        designStudioMode: ctx.mode,
      };
      emitPageAction(EventType.FETCHING_LATEST_VERSION_DONE, attrs);
    },
    sendSavingToast: ({ addToast, removeToast, translator }) => {
      const savingTextKey = window?.navigator?.onLine
        ? 'app.saving'
        : 'app.savingOffline';

      const bread = {
        title: translator(savingTextKey),
        type: ToastType.PASSIVE,
        spinner: true,
        onPress: removeToast,
      };
      addToast?.(bread);
    },
    updateUndoRedoState: send('UPDATE_UNDO_REDO_STATE'),
    updateCanUndoAndRedo: assign(ctx => ({
      canUndo: isNotBottomOfStack(ctx),
      canRedo: isNotTopOfStack(ctx),
    })),
    // log error/exception to Sentry
    logError: sharedOptions.logError,
    setupCompletion: assign((_: Context, event: Event) => {
      const { user, email, buildingModel, userId, partnerId } = (
        event as SetupCompletionEvent
      ).data;

      return {
        buildingModel,
        userId,
        email,
        user,
        partnerId,
      };
    }),
    setHistory: assign((_: Context, event: Event) => {
      const history: VersionDocument[] = [];
      let currentIndexInHistory;

      (event as SetHistoryEvent).data.forEach((versionDocument, index) => {
        // if theres already a current index, keep that as version
        // otherwise use the last index as current version
        const versionData = versionDocument.data();

        /**
         * setHistory gets called at load and anytime any chnages are made to the model. We want to ensure that all designCOnfig's in the history stack
         * are of the same format.
         * At load time when this functions gets called for the first time its possible that ctx.templateDesignConfigFlag is undefined, and has not been fetched.
         */
        const designConfig = versionData.design_config as Structure;

        if (versionData.current) {
          currentIndexInHistory = index;
        }

        const version = {
          ...versionData,
          design_config: designConfig,
          ref: versionDocument.ref,
          id: versionDocument.id,
        };

        history.push(version as VersionDocument);
      });

      if (currentIndexInHistory === undefined) {
        currentIndexInHistory = history.length === 0 ? 0 : history.length - 1;
      }

      if (history.length !== undefined) {
        const design = history[currentIndexInHistory];
        // Need to make sure we are forward compatible to any changes to the design config
        // so use EmptyDesignConfig as basis then over lay any changes over it.

        const currentDesignConfig = design?.design_config;

        return {
          currentVersion: design?.id,
          currentIndexInHistory,
          designConfig: currentDesignConfig,
          history,
        };
      }

      return {};
    }),
    assignInspireMeImage: assign((ctx: Context, event: Event) => {
      const { image, designNameToRestore } = (event as ClearInspireMeImage)
        .data;

      const viewer = macheteViewerRef.current;
      const building = macheteBuildingRef.current;
      // Restore opacity of model and turn off outlines; restore environment.
      if (building && image !== '') {
        building.designer.view_design(ctx.inspireMeDesignNameToRestore);
        restoreOpacityAndHideOutlines(building);
      }
      if (viewer && image !== '') {
        showEnvironment(viewer);
      }

      ctx.inspireMePastImageURLs.push(image);

      return {
        inspireMeImageURL: image,
        inspireMeDesignNameToRestore: designNameToRestore,
        inspireMeFrozenRenderURL: '',
      };
    }),
    assignFrozenInspireMeImage: assign((_: Context, event: Event) => {
      const { frozenRenderURL } = (event as WaitForInspireMeImage).data;
      return {
        inspireMeFrozenRenderURL: frozenRenderURL,
      };
    }),
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    clearInspireMeImage: assign((ctx: Context, _: Event) => {
      const viewer = macheteViewerRef.current;
      const building = macheteBuildingRef.current;
      // Restore opacity of model and turn off outlines; restore environment.
      if (building && ctx.inspireMeImageURL !== '') {
        building.designer.view_design(ctx.inspireMeDesignNameToRestore);
        restoreOpacityAndHideOutlines(building);
      }
      if (viewer && ctx.inspireMeImageURL !== '') {
        showEnvironment(viewer);
      }
      return { inspireMeImageURL: '', inspireMeFrozenRenderURL: '' };
    }),
    setInspireMeImage: assign((_: Context, event: Event) => {
      const { image } = (event as SetInspireMeImage).data;
      return { inspireMeImageURL: image };
    }),
    updateDesignConfigFromBuilding: assign((ctx: Context) => {
      let { designConfig } = ctx;
      const building = macheteBuildingRef.current;

      if (building) {
        designConfig = getCurrentDesignConfig(building.designer);
      }

      return { designConfig };
    }),
    undoDesignAction: assign((ctx: Context) => optimisticUpdate(ctx, -1)),
    redoDesignAction: assign((ctx: Context) => optimisticUpdate(ctx, 1)),
    teardownFirebaseApp: () => {
      // TODO This assumes history is the only place we will use the FirebaseClient,
      //  instead consider implementing a FirebaseClient "reference counter" to determine
      //  when to call teardownApp
      FirebaseClient.teardownApp();
      return true;
    },
  },
  delays: {},
  activities: {},
};
export { fetchCurrentVersion, options, createVersion };
